<template>
  <div class="mobile-nav" v-if="showNav">
    <button class="hamburger-btn" @click="openMenu" aria-label="Open navigation menu">
      <nav-icon name="menu" :size="22" />
    </button>

    <transition name="drawer">
      <div v-if="menuOpen" class="drawer-overlay" @click.self="closeMenu">
        <nav class="drawer">
          <div class="drawer-header">
            <div class="brand"><span class="logo">✨</span><span class="title">Astro Way</span></div>
            <button class="close-btn" @click="closeMenu" aria-label="Close menu"><nav-icon name="close" :size="22" /></button>
          </div>

          <div class="drawer-search">
            <nav-icon name="search" :size="16" class="search-ic" />
            <input v-model="query" type="text" placeholder="Search pages…" aria-label="Search pages" />
            <button v-if="query" class="clear-btn" @click="clearQuery" aria-label="Clear search"><nav-icon name="close" :size="15" /></button>
          </div>

          <div class="drawer-scroll">
            <div v-for="group in filteredGroups" :key="group.id" class="m-group">
              <button class="m-group-header" @click="toggleGroup(group.id)" :aria-expanded="isGroupOpen(group.id)">
                <nav-icon :name="group.icon" :size="18" class="g-ic" />
                <span class="g-label">{{ group.label }}</span>
                <nav-icon name="chevron" :size="16" class="g-caret" :class="{ open: isGroupOpen(group.id) }" />
              </button>
              <div class="m-items-wrap" :class="{ open: isGroupOpen(group.id) }">
                <div class="m-items">
                  <router-link v-for="item in group.items" :key="item.to" :to="item.to" class="m-link" @click="closeMenu">
                    <nav-icon :name="item.icon" :size="20" />
                    <span>{{ item.label }}</span>
                  </router-link>
                </div>
              </div>
            </div>
            <p v-if="!filteredGroups.length" class="m-no-match">No pages match “{{ query }}”.</p>
          </div>
        </nav>
      </div>
    </transition>
  </div>
</template>

<script>
import { ref, computed, watch } from 'vue'
import { useRoute } from 'vue-router'
import NavIcon from './NavIcon.vue'
import { publicRouteNames } from '../nav'
import { useNavGroups } from '../composables/useNavGroups'

export default {
  name: 'MobileNav',
  components: { NavIcon },
  setup() {
    const route = useRoute()
    const menuOpen = ref(false)

    // Show on every admin surface; hide only on public/end-user routes. New admin
    // pages get the nav automatically (no hardcoded allow-list to fall out of date).
    const showNav = computed(() => !publicRouteNames.includes(route.name))

    const openMenu = () => { menuOpen.value = true }
    const closeMenu = () => { menuOpen.value = false }

    const { query, filteredGroups, isGroupOpen, toggleGroup, clearQuery } = useNavGroups('mobileOpenGroups')

    // Close the drawer whenever the route changes (belt-and-suspenders with the
    // per-link @click close).
    watch(() => route.fullPath, () => { menuOpen.value = false })

    return { menuOpen, showNav, openMenu, closeMenu, query, filteredGroups, isGroupOpen, toggleGroup, clearQuery }
  }
}
</script>

<style scoped>
.mobile-nav { position: fixed; top: 12px; left: 12px; z-index: 999; }

.hamburger-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 44px;
  height: 44px;
  background: rgba(18, 18, 34, 0.85);
  backdrop-filter: blur(10px);
  -webkit-backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.12);
  border-radius: 12px;
  color: var(--text-primary);
  cursor: pointer;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.3);
}

.drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.6);
  z-index: 1000;
  display: flex;
  justify-content: flex-start;
}

.drawer {
  width: min(340px, 86vw);
  height: 100%;
  background: #14131f;
  display: flex;
  flex-direction: column;
  box-shadow: 6px 0 24px rgba(0, 0, 0, 0.45);
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px 16px 14px;
  border-bottom: 1px solid rgba(255, 255, 255, 0.08);
}
.brand { display: flex; align-items: center; gap: 10px; }
.brand .logo { font-size: 22px; }
.brand .title { font-size: 17px; font-weight: 600; color: var(--text-primary); }
.close-btn {
  display: flex; align-items: center; justify-content: center;
  width: 40px; height: 40px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 10px;
  color: var(--text-secondary);
  cursor: pointer;
}
.close-btn:hover { color: var(--text-primary); }

.drawer-search { position: relative; display: flex; align-items: center; padding: 12px 16px; }
.drawer-search .search-ic { position: absolute; left: 28px; color: var(--text-secondary); pointer-events: none; }
.drawer-search input {
  width: 100%;
  padding: 11px 36px 11px 40px;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 10px;
  color: var(--text-primary);
  font-size: 16px;
}
.drawer-search input:focus { outline: none; border-color: var(--primary-color); box-shadow: 0 0 0 2px rgba(108, 92, 231, 0.2); }
.drawer-search .clear-btn {
  position: absolute; right: 24px;
  display: flex; align-items: center; justify-content: center;
  width: 26px; height: 26px;
  background: none; border: none; color: var(--text-secondary); cursor: pointer;
}

.drawer-scroll { flex: 1; overflow-y: auto; padding: 4px 10px 24px; }

.m-group { margin-bottom: 2px; }
.m-group-header {
  width: 100%;
  min-height: 48px;
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 10px;
  background: none;
  border: none;
  color: var(--text-secondary);
  font-family: inherit;
  font-size: 12px;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  cursor: pointer;
  border-radius: 10px;
}
.m-group-header:hover { color: var(--text-primary); background: rgba(255, 255, 255, 0.04); }
.g-ic { opacity: 0.85; }
.g-label { flex: 1; text-align: left; }
.g-caret { opacity: 0.6; transition: transform 0.2s ease; }
.g-caret.open { transform: rotate(180deg); }

.m-items-wrap { display: grid; grid-template-rows: 0fr; transition: grid-template-rows 0.24s ease; }
.m-items-wrap.open { grid-template-rows: 1fr; }
.m-items { overflow: hidden; display: flex; flex-direction: column; }

.m-link {
  display: flex;
  align-items: center;
  gap: 13px;
  min-height: 48px;
  padding: 11px 12px 11px 16px;
  border-radius: 10px;
  text-decoration: none;
  color: var(--text-primary);
  font-size: 16px;
  font-weight: 500;
}
.m-link:active { background: rgba(255, 255, 255, 0.08); }
.m-link.router-link-exact-active {
  background: var(--primary-color);
  color: #fff;
}
.m-no-match { padding: 16px; font-size: 14px; color: var(--text-secondary); }

/* Transition: overlay fades, drawer slides in */
.drawer-enter-active, .drawer-leave-active { transition: opacity 0.25s ease; }
.drawer-enter-from, .drawer-leave-to { opacity: 0; }
.drawer-enter-active .drawer, .drawer-leave-active .drawer { transition: transform 0.25s ease; }
.drawer-enter-from .drawer, .drawer-leave-to .drawer { transform: translateX(-100%); }

@media (min-width: 1025px) {
  .mobile-nav { display: none; }
}
</style>
